import { inject, Injectable } from '@angular/core';
import { AccountType } from '@mkp/account/data-access';
import {
  LegalEntity,
  LegalEntityCheUidPostBody,
  LegalEntityDto,
} from '@mkp/legal-entity/data-access';
import { IndustryDto, toUUID } from '@mkp/shared/models';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { OnboardingApiService } from './onboarding-api.service';
import { CompanyProfileLink } from '@mkp/company/models';

export interface CreateCompanyInput {
  industryId: IndustryDto['id'];
  employeeCountFrom: LegalEntity['employeeCountFrom'];
  employeeCountTo: LegalEntity['employeeCountTo'];
  externalSource: LegalEntity['externalSource'];
  externalId?: string;
  name: LegalEntity['name'];
  state: LegalEntity['state'];
  address: LegalEntity['address'];
  companyWebsite: CompanyProfileLink['url'];
  accountType?: AccountType;
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private onboardingApiService = inject(OnboardingApiService);

  /**
   * Claims a company by CHE UID using MoneyHouse.
   *
   * @param claimCompanyDto - The DTO containing the CHE UID and other details.
   * @param withMembership - Create account membership for the logged-in user.
   * @returns An observable of the claimed LegalEntityDto.
   */
  public claimCompanyByCheUid(
    claimCompanyDto: LegalEntityCheUidPostBody,
    withMembership: boolean
  ): Observable<LegalEntityDto> {
    return this.processClaimFlow(
      this.onboardingApiService.createLegalEntityByCheUid(claimCompanyDto),
      withMembership
    );
  }

  /**
   * Claims a company from input data.
   *
   * @param createCompanyInput - Data from the input form.
   * @param withMembership - Create account membership for the logged-in user.
   * @returns An observable of the claimed LegalEntityDto.
   */
  public claimCompanyFromInput(
    createCompanyInput: CreateCompanyInput,
    withMembership: boolean
  ): Observable<LegalEntityDto> {
    const { companyWebsite, accountType, ...legalEntityPostBody } = createCompanyInput;
    return this.processClaimFlow(
      this.onboardingApiService.createLegalEntity(legalEntityPostBody),
      withMembership,
      companyWebsite,
      accountType
    );
  }

  public activateCompany(legalEntityDto: LegalEntityDto): Observable<LegalEntityDto> {
    return this.onboardingApiService.activateLegalEntity(legalEntityDto);
  }

  public processClaimFlow(
    legalEntity$: Observable<LegalEntityDto>,
    withMembership: boolean,
    companyWebsite?: CompanyProfileLink['url'],
    accountType?: AccountType
  ): Observable<LegalEntityDto> {
    return legalEntity$.pipe(
      switchMap((createdLegalEntity) =>
        this.processAccountFlow(createdLegalEntity, withMembership, accountType)
      ),
      switchMap((createdLegalEntity) =>
        this.processCompanyProfileFlow(createdLegalEntity, companyWebsite)
      )
    );
  }

  public processCompanyProfileFlow(
    legalEntityDto: LegalEntityDto,
    companyWebsite?: CompanyProfileLink['url']
  ): Observable<LegalEntityDto> {
    return this.onboardingApiService.createCompanyProfile(legalEntityDto, companyWebsite).pipe(
      switchMap((companyProfile) =>
        this.onboardingApiService
          .createLocation(legalEntityDto, companyProfile)
          .pipe(
            switchMap((location) =>
              this.onboardingApiService
                .patchCompanyProfileMainLocation(companyProfile, location.id)
                .pipe(catchError(() => of(companyProfile)))
            )
          )
      ),
      map(() => legalEntityDto)
    );
  }

  private processAccountFlow(
    legalEntityDto: LegalEntityDto,
    withMembership: boolean,
    accountType?: AccountType
  ): Observable<LegalEntityDto> {
    return this.onboardingApiService.createAccount(legalEntityDto, accountType).pipe(
      switchMap((accountDto) =>
        withMembership
          ? this.onboardingApiService.createMembership(accountDto)
          : of({ accountId: toUUID(accountDto.id) })
      ),
      switchMap(({ accountId }) => this.onboardingApiService.createWallet(toUUID(accountId))),
      map(() => legalEntityDto)
    );
  }
}
